import { __rest } from "tslib";
import * as React from "react";
import { Bold, Blocks, EyeIcon, Image as ImageIcon, Italic, Link2, List, ListOrdered, PenIcon, Quote, SquareCode, } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Markdown } from "../../components/ui/markdown";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent } from "../../components/ui/tabs";
function noDefault(e) {
    e.preventDefault();
}
function stringifyTextareaValue(value) {
    if (Array.isArray(value)) {
        return value.join("\n");
    }
    if (typeof value === "number") {
        return value.toString();
    }
    if (typeof value === "string") {
        return value;
    }
    return "";
}
function formatInlineSelection(value, selectionStart, selectionEnd, prefix, suffix = prefix) {
    const start = value.slice(0, selectionStart);
    const selected = value.slice(selectionStart, selectionEnd);
    const end = value.slice(selectionEnd);
    const nextValue = `${start}${prefix}${selected}${suffix}${end}`;
    if (selectionStart === selectionEnd) {
        const caret = selectionStart + prefix.length;
        return { value: nextValue, selectionStart: caret, selectionEnd: caret };
    }
    return {
        value: nextValue,
        selectionStart: selectionStart + prefix.length,
        selectionEnd: selectionEnd + prefix.length,
    };
}
function formatLinePrefixSelection(value, selectionStart, selectionEnd, prefix) {
    const lineStart = value.lastIndexOf("\n", Math.max(0, selectionStart - 1)) + 1;
    const lineEndIndex = value.indexOf("\n", selectionEnd);
    const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;
    const start = value.slice(0, lineStart);
    const selectedLines = value.slice(lineStart, lineEnd) || "";
    const end = value.slice(lineEnd);
    const prefixedLines = selectedLines
        .split("\n")
        .map((line) => `${prefix}${line}`)
        .join("\n");
    const nextValue = `${start}${prefixedLines}${end}`;
    if (selectionStart === selectionEnd) {
        const caret = selectionStart + prefix.length;
        return { value: nextValue, selectionStart: caret, selectionEnd: caret };
    }
    const insertedChars = prefixedLines.length - selectedLines.length;
    return {
        value: nextValue,
        selectionStart: selectionStart + prefix.length,
        selectionEnd: selectionEnd + insertedChars,
    };
}
function formatWrappedPlaceholder(value, selectionStart, selectionEnd, prefix, suffix, placeholder) {
    if (selectionStart !== selectionEnd) {
        return formatInlineSelection(value, selectionStart, selectionEnd, prefix, suffix);
    }
    const start = value.slice(0, selectionStart);
    const end = value.slice(selectionEnd);
    const nextValue = `${start}${prefix}${placeholder}${suffix}${end}`;
    const placeholderStart = selectionStart + prefix.length;
    return {
        value: nextValue,
        selectionStart: placeholderStart,
        selectionEnd: placeholderStart + placeholder.length,
    };
}
function formatLinkSelection(value, selectionStart, selectionEnd) {
    const selected = value.slice(selectionStart, selectionEnd);
    const text = selected || "Linktext";
    const href = "https://";
    return formatWrappedPlaceholder(value, selectionStart, selectionEnd, "[", `](${href})`, text);
}
function formatImageSelection(value, selectionStart, selectionEnd) {
    const selected = value.slice(selectionStart, selectionEnd);
    const alt = selected || "Bildbeschreibung";
    const src = "https://";
    return formatWrappedPlaceholder(value, selectionStart, selectionEnd, "![", `](${src})`, alt);
}
function formatListSelection(value, selectionStart, selectionEnd) {
    return formatLinePrefixSelection(value, selectionStart, selectionEnd, "- ");
}
function formatQuoteSelection(value, selectionStart, selectionEnd) {
    return formatLinePrefixSelection(value, selectionStart, selectionEnd, "> ");
}
export const markdownControls = [
    {
        label: "Fett",
        icon: Bold,
        shortcut: "Mod+B",
        apply: (value, selectionStart, selectionEnd) => formatInlineSelection(value, selectionStart, selectionEnd, "**"),
    },
    {
        label: "Kursiv",
        icon: Italic,
        shortcut: "Mod+I",
        apply: (value, selectionStart, selectionEnd) => formatInlineSelection(value, selectionStart, selectionEnd, "*"),
    },
    {
        label: "Hyperlink",
        icon: Link2,
        shortcut: "Mod+K",
        apply: (value, selectionStart, selectionEnd) => formatLinkSelection(value, selectionStart, selectionEnd),
    },
    {
        label: "Bild",
        icon: ImageIcon,
        apply: (value, selectionStart, selectionEnd) => formatImageSelection(value, selectionStart, selectionEnd),
    },
    {
        label: "Zitat",
        icon: Quote,
        apply: (value, selectionStart, selectionEnd) => formatQuoteSelection(value, selectionStart, selectionEnd),
    },
    {
        label: "Code",
        icon: SquareCode,
        apply: (value, selectionStart, selectionEnd) => formatInlineSelection(value, selectionStart, selectionEnd, "`"),
    },
    {
        label: "Aufzählung",
        icon: List,
        apply: (value, selectionStart, selectionEnd) => formatListSelection(value, selectionStart, selectionEnd),
    },
    {
        label: "Aufzählung (nummeriert)",
        icon: ListOrdered,
        apply: (value, selectionStart, selectionEnd) => formatLinePrefixSelection(value, selectionStart, selectionEnd, "1. "),
    },
];
const markdownShortcuts = new Map(markdownControls
    .filter((control) => control.shortcut)
    .map((control) => { var _a; return [(_a = control.shortcut) === null || _a === void 0 ? void 0 : _a.toLowerCase(), control]; }));
function resizeImage(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            var _a;
            const img = new Image();
            img.onload = () => {
                const originalWidth = img.width;
                const originalHeight = img.height;
                const maxPixels = 1000 * 600;
                const currentPixels = originalHeight * originalWidth;
                const scale = Math.sqrt(Math.min(1, maxPixels / currentPixels));
                const width = ~~(scale * originalWidth);
                const height = ~~(scale * originalHeight);
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                ctx.imageSmoothingQuality = "high";
                ctx.imageSmoothingEnabled = true;
                canvas.width = width;
                canvas.height = height;
                ctx === null || ctx === void 0 ? void 0 : ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL("image/png"));
            };
            img.src = (_a = e.target) === null || _a === void 0 ? void 0 : _a.result;
        };
        reader.readAsDataURL(file);
    });
}
function handleImagePaste(e, images) {
    const textarea = e.currentTarget;
    for (let i = 0; i < e.clipboardData.files.length; ++i) {
        const file = e.clipboardData.files[i];
        const id = (~~(10000 * Math.random())).toString(16);
        textarea.focus();
        resizeImage(file).then((uri) => images.set(id, uri));
        document.execCommand("insertText", false, `![${file.name}](#!${id})`);
    }
}
function applyMarkdownChanges(desc, images) {
    images.forEach((uri, id) => {
        desc = desc.replace(`](#!${id})`, `](${uri})`);
    });
    return desc;
}
export function useUploadImages() {
    return React.useMemo(() => {
        const images = new Map();
        const apply = (value) => applyMarkdownChanges(value, images);
        return [images, apply];
    }, []);
}
function setTextareaValue(textarea, nextValue) {
    var _a;
    const descriptor = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value");
    (_a = descriptor === null || descriptor === void 0 ? void 0 : descriptor.set) === null || _a === void 0 ? void 0 : _a.call(textarea, nextValue);
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
}
export const MarkdownTextarea = (_a) => {
    var { images, autoResize = false, className, defaultValue, noResize = false, onChange, onPaste, style, value, initialMode = "text" } = _a, props = __rest(_a, ["images", "autoResize", "className", "defaultValue", "noResize", "onChange", "onPaste", "style", "value", "initialMode"]);
    const textareaRef = React.useRef(null);
    const selectionRef = React.useRef({ start: 0, end: 0 });
    const shouldRestoreSelectionRef = React.useRef(false);
    const [mode, setMode] = React.useState(initialMode);
    const [draft, setDraft] = React.useState(() => stringifyTextareaValue(value !== null && value !== void 0 ? value : defaultValue));
    React.useEffect(() => {
        if (value !== undefined) {
            setDraft(stringifyTextareaValue(value));
        }
    }, [value]);
    const currentValue = value === undefined ? draft : stringifyTextareaValue(value);
    const previewValue = React.useMemo(() => applyMarkdownChanges(currentValue, images), [currentValue, images]);
    const rememberSelection = React.useCallback(() => {
        var _a, _b;
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }
        selectionRef.current = {
            start: (_a = textarea.selectionStart) !== null && _a !== void 0 ? _a : textarea.value.length,
            end: (_b = textarea.selectionEnd) !== null && _b !== void 0 ? _b : textarea.value.length,
        };
    }, []);
    React.useEffect(() => {
        if (mode !== "text" || !shouldRestoreSelectionRef.current) {
            return;
        }
        shouldRestoreSelectionRef.current = false;
        requestAnimationFrame(() => {
            const textarea = textareaRef.current;
            if (!textarea) {
                return;
            }
            textarea.focus();
            textarea.setSelectionRange(selectionRef.current.start, selectionRef.current.end);
        });
    }, [mode]);
    const handleChange = React.useCallback((e) => {
        setDraft(e.target.value);
        onChange === null || onChange === void 0 ? void 0 : onChange(e);
    }, [onChange]);
    const handlePaste = React.useCallback((e) => {
        onPaste === null || onPaste === void 0 ? void 0 : onPaste(e);
        if (e.defaultPrevented) {
            return;
        }
        handleImagePaste(e, images);
    }, [images, onPaste]);
    const applyFormattedSelection = React.useCallback((formatted) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }
        setDraft(formatted.value);
        setTextareaValue(textarea, formatted.value);
        selectionRef.current = {
            start: formatted.selectionStart,
            end: formatted.selectionEnd,
        };
        requestAnimationFrame(() => {
            const currentTextarea = textareaRef.current;
            if (!currentTextarea) {
                return;
            }
            currentTextarea.focus();
            currentTextarea.setSelectionRange(formatted.selectionStart, formatted.selectionEnd);
        });
    }, []);
    const applyControl = React.useCallback((control) => {
        var _a, _b;
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }
        textarea.focus();
        const formatted = control.apply(textarea.value, (_a = textarea.selectionStart) !== null && _a !== void 0 ? _a : textarea.value.length, (_b = textarea.selectionEnd) !== null && _b !== void 0 ? _b : textarea.value.length);
        applyFormattedSelection(formatted);
    }, [applyFormattedSelection]);
    const openComponentCatalog = React.useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }
        const { value, selectionStart, selectionEnd } = textarea;
        window.dispatchEvent(new CustomEvent("open-dialog", {
            detail: {
                name: "component-catalog",
                data: {
                    textarea,
                    value,
                    selectionStart,
                    selectionEnd,
                    insertMarkdown(source) {
                        if (textareaRef.current !== textarea || !textarea.isConnected) {
                            throw new Error("The markdown editor is no longer available.");
                        }
                        if (textarea.value !== value) {
                            throw new Error("The markdown source has changed since the catalog was opened.");
                        }
                        const caret = selectionStart + source.length;
                        applyFormattedSelection({
                            value: `${value.slice(0, selectionStart)}${source}${value.slice(selectionEnd)}`,
                            selectionStart: caret,
                            selectionEnd: caret,
                        });
                    },
                },
            },
        }));
    }, [applyFormattedSelection]);
    const handleKeyDown = React.useCallback((e) => {
        var _a;
        const shortcut = `${e.metaKey || e.ctrlKey ? "mod+" : ""}${e.key.toLowerCase()}`;
        const control = markdownShortcuts.get(shortcut);
        if (control) {
            noDefault(e);
            applyControl(control);
            e.stopPropagation();
        }
        (_a = props.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(props, e);
    }, [applyControl, props]);
    const handleModeChange = React.useCallback((nextMode) => {
        if (nextMode === mode) {
            return;
        }
        else if (nextMode === "preview") {
            rememberSelection();
        }
        else if (nextMode === "text") {
            shouldRestoreSelectionRef.current = true;
        }
        else {
            return;
        }
        setMode(nextMode);
    }, [mode, rememberSelection]);
    return (React.createElement(Tabs, { className: "w-full", value: mode, onValueChange: handleModeChange },
        React.createElement("div", { className: "overflow-hidden rounded-md border border-input bg-background" },
            React.createElement(TabsContent, { className: "mt-0", value: "text" },
                React.createElement("div", { className: "flex flex-wrap gap-2 border-b border-input bg-muted/40 px-2 py-2 items-center" },
                    React.createElement(Button, { "aria-label": "Vorschau", className: "h-8 w-8", onClick: () => setMode("preview"), title: "Vorschau anzeigen", size: "icon", variant: "ghost" },
                        React.createElement(EyeIcon, { className: "h-4 w-4" })),
                    markdownControls.map((control) => (React.createElement(Button, { key: control.label, "aria-label": control.label, onMouseDown: noDefault, onClick: () => applyControl(control), disabled: props.disabled || props.readOnly, className: "h-8 w-8", size: "icon", title: control.shortcut
                            ? `${control.label} (${control.shortcut})`
                            : control.label, type: "button", variant: "ghost" },
                        React.createElement(control.icon, { className: "h-4 w-4" })))),
                    React.createElement(Button, { "aria-label": "Komponentkatalog", className: "h-8 w-8", disabled: props.disabled || props.readOnly, onMouseDown: noDefault, onClick: openComponentCatalog, size: "icon", title: "Komponentkatalog", type: "button", variant: "ghost" },
                        React.createElement(Blocks, { className: "h-4 w-4" }))),
                React.createElement(Textarea, Object.assign({}, props, { autoResize: autoResize, className: cn("min-h-[220px] rounded-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0", className, autoResize
                        ? "resize-none overflow-y-hidden"
                        : cn("overflow-y-auto", noResize ? "resize-none" : "resize-y")), onChange: handleChange, onClick: rememberSelection, onKeyDown: handleKeyDown, onKeyUp: rememberSelection, onPaste: handlePaste, onSelect: rememberSelection, ref: textareaRef, style: Object.assign(Object.assign({}, style), { resize: autoResize || noResize ? "none" : style === null || style === void 0 ? void 0 : style.resize }), value: currentValue }))),
            React.createElement(TabsContent, { className: "mt-0", value: "preview" },
                React.createElement("div", { className: "flex flex-wrap gap-2 border-b border-input bg-muted/40 px-2 py-2 items-center" },
                    React.createElement(Button, { "aria-label": "Verfassen", className: "h-8 w-8", onClick: () => setMode("text"), title: "Verfassen", size: "icon", variant: "ghost" },
                        React.createElement(PenIcon, { className: "h-4 w-4" }))),
                React.createElement("div", { className: cn("min-h-[220px] px-3 py-2 max-w-none", className) }, previewValue ? (React.createElement(Markdown, { content: previewValue })) : (React.createElement("p", { className: "text-muted-foreground italic" }, "Keine Vorschau verf\u00FCgbar.")))))));
};
//# sourceMappingURL=MarkdownTextarea.js.map